import CreateItemButton from "components/buttons/create";
import List from "components/list";
import ProjectFilters from "./filters";
import { EmployeeCell } from "components/cell/employee/cell";
import { getEmployeeFields } from "components/user/config";
import { StatusCell } from "@/components/status/cell";
import { DateCell } from "@/components/date/cell";
import ProjectNameCell from "@/modules/work/projects/cells/name";
import { usePermissions } from "contexts/permission";
import HeaderPortal from "components/portal/header-portal";
import { useQuery } from "@tanstack/react-query";
import { getItemsFromCollection } from "directus/src/operations";
import { PermissionGate } from "permissions";
import { ROUTE } from "@/lib/utils";

const PendingJobsCell = ({ projectId }) => {
  const { data } = useQuery({
    queryKey: ["pending-jobs-count", projectId],
    queryFn: () =>
      getItemsFromCollection("jobs", {
        filter: {
          project: { _eq: projectId },
          status: { _nin: ["closed", "cancelled", "archived"] },
        },
        aggregate: { countDistinct: ["id"] },
      }),
    enabled: !!projectId,
  });

  const count = data?.[0]?.countDistinct?.id || 0;
  return <span>{count}</span>;
};

const PendingBillablesCell = ({ projectId }) => {
  const { data } = useQuery({
    queryKey: ["pending-billables-count", projectId],
    queryFn: () =>
      getItemsFromCollection("billables", {
        filter: {
          job: { project: { _eq: projectId } },
          status: { _in: ["contracted", "pending_invoice"] },
        },
        aggregate: { countDistinct: ["id"] },
      }),
    enabled: !!projectId,
  });

  const count = data?.[0]?.countDistinct?.id || 0;
  return <span>{count}</span>;
};

export default function Projects({
  defaultFilters = {},
  storageKey = "projects",
  title = "Projects",
}) {
  const permissions = usePermissions();

  const columns = [
    {
      id: "name",
      header: "Name",
      accessorKey: "name",
      cell: ProjectNameCell,
      sortable: true,
    },
    {
      id: "pc",
      header: "PC",
      accessorKey: "coordinator",
      cell: EmployeeCell,
    },
    {
      id: "am",
      header: "AM",
      accessorKey: "account_manager",
      cell: EmployeeCell,
    },
    {
      id: "sc",
      header: "SC",
      accessorKey: "sales_coordinator",
      cell: EmployeeCell,
    },
    {
      id: "status",
      header: "Status",
      accessorKey: "status",
      cell: StatusCell,
    },
    {
      id: "job-count",
      header: "Pending Jobs",
      accessorKey: "id",
      cell: ({ getValue }) => <PendingJobsCell projectId={getValue()} />,
    },
    {
      id: "billables-count",
      header: "Pending Billables",
      accessorKey: "id",
      cell: ({ getValue }) => <PendingBillablesCell projectId={getValue()} />,
      collection: "billables",
    },
    {
      id: "date_created",
      header: "Created",
      accessorKey: "date_created",
      cell: DateCell,
    },
  ];

  const fields = [
    "id",
    "name",
    "project_id",
    getEmployeeFields("coordinator"),
    "client.first_name",
    "client.last_name",
    "client.company_id.name",
    getEmployeeFields("account_manager"),
    getEmployeeFields("sales_coordinator"),
    "status",
    "date_created",
    "logo",
  ];

  const filters = {
    status: {
      _eq: "active",
    },

    ...defaultFilters,
  };

  return (
    <div className="w-full">
      <HeaderPortal>
        <PermissionGate rule={"create:*"} collection="projects">
          <CreateItemButton href={ROUTE.PROJECT_NEW()}>
            Project
          </CreateItemButton>
        </PermissionGate>
      </HeaderPortal>
      <div className="flex-1 h-full">
        <List
          title={title}
          endPoint="projects"
          storageKey={storageKey}
          filters={filters}
          columns={columns.filter((column) => {
            if (!column?.collection) return true;
            return permissions.hasCollectionPermission(column.collection);
          })}
          meta={{
            fields,
          }}
          href={(item) => `/work/projects/${item.project_id}`}
          renderFilters={ProjectFilters}
        />
      </div>
    </div>
  );
}
